<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (entity(); as e) {
        <a class="page-head__crumb" [routerLink]="['/entity', e.short_id, 'dashboard']">{{
          e.name
        }}</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
      }
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Debtors Finance</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles page-head__titles--icon">
        <span class="page-head__icon" aria-hidden="true"><app-icon name="trending-up" [size]="24" /></span>
        <h1 class="page-head__title">Debtors Finance</h1>
        @if (!isLoading() && !loadError()) {
          <p class="page-head__subtitle">
            Your Debtors Finance facilities for this business, and where each one stands.
            {{ facilities().length }} in total.
          </p>
        }
      </div>
      <!-- Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application; a draft already started still resumes. -->
      @if (
        !isLoading() && !loadError() && facilities().length > 0 && (openDraft() || !directApplicationsPaused)
      ) {
        <div class="page-head__actions">
          <button
            mat-flat-button
            color="primary"
            type="button"
            (click)="startOrResume()"
            data-testid="new-df-btn"
          >
            <app-icon name="plus" [size]="18" />
            <span>{{ openDraft() ? 'Continue your draft' : 'New DF facility' }}</span>
          </button>
        </div>
      }
    </div>
  </header>

  @if (isLoading()) {
    <mat-card
      appearance="outlined"
      class="table-card"
      aria-label="Loading Debtors Finance facilities"
    >
      <table class="applications-table sg-table--skeleton">
        <thead>
          <tr>
            <th>Business</th>
            <th>Requested limit</th>
            <th>Status</th>
            <th>Stage</th>
            <th>Started</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (r of [1, 2, 3, 4, 5]; track r) {
            <tr>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span></td>
              <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
              <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
              <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
              <td></td>
            </tr>
          }
        </tbody>
      </table>
    </mat-card>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load facilities</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="reload()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (facilities().length === 0) {
    <div class="sg-empty sg-empty--bordered">
      <span class="sg-empty__art" style="--art-src: url(/assets/illustrations/debtors-finance.svg)" aria-hidden="true"></span>
      <p class="sg-empty__title">No Debtors Finance facilities yet</p>
      <p class="sg-empty__desc">Apply for a Debtors Finance facility and it will appear here.</p>
      <!-- Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. -->
      @if (!directApplicationsPaused) {
        <a mat-flat-button color="primary" [routerLink]="['/entity', entityId, 'df', 'new']">
          Apply now
        </a>
      }
    </div>
  } @else {
    <mat-card appearance="outlined" class="table-card">
      <table mat-table [dataSource]="facilities()" class="applications-table">
        <ng-container matColumnDef="entity">
          <th mat-header-cell *matHeaderCellDef>Business</th>
          <td mat-cell *matCellDef="let f">{{ f.entity_name }}</td>
        </ng-container>

        <ng-container matColumnDef="requested_limit">
          <th mat-header-cell *matHeaderCellDef>Requested limit</th>
          <td mat-cell *matCellDef="let f">{{ f.requested_limit | zar }}</td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let f">
            <app-status-chip [status]="f.df_status" />
          </td>
        </ng-container>

        <ng-container matColumnDef="stage">
          <th mat-header-cell *matHeaderCellDef>Stage</th>
          <td mat-cell *matCellDef="let f">
            <app-status-chip [status]="f.application_stage" />
          </td>
        </ng-container>

        <ng-container matColumnDef="started">
          <th mat-header-cell *matHeaderCellDef>Started</th>
          <td mat-cell *matCellDef="let f">
            <span
              class="applications-table__dates"
              [matTooltip]="activityTooltip(f)"
              matTooltipClass="tooltip--multiline"
            >
              @if (f.application_stage === 'DRAFT') {
                Started {{ f.created_at | ago }}
              } @else {
                {{ f.created_at | date: 'mediumDate' | dash }}
              }
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let f" class="applications-table__action-cell">
            @if (f.application_stage === 'DRAFT') {
              <a
                mat-stroked-button
                [routerLink]="['/entity', entityId, 'df', f.short_id]"
                [attr.aria-label]="'Continue your ' + f.entity_name + ' draft facility'"
                (click)="$event.stopPropagation()"
              >
                Continue
              </a>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="columns"></tr>
        <tr
          mat-row
          *matRowDef="let f; columns: columns"
          class="applications-table__row"
          [routerLink]="['/entity', entityId, 'df', f.short_id]"
        ></tr>
      </table>
    </mat-card>
  }
</div>
